import { inject } from '@angular/core';
import { ClubService, SurveyService, TagsService } from '@core/services';
import { GroupEntity, Pagination, SurveyAnswerDetailsEntity, SurveyEntity, SurveyResultDetailsEntity, SurveyResultsEntity, SurveysList, TagEntity } from '@core/entities';
import { FilterQuery } from '@core/types';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { plainToClass, plainToInstance } from 'class-transformer';
import { SectionType, TagType } from '@core/enums';
import { GenerateImageUrlWithToken, GetTeamId, GetUserId } from '@core/utils';
import { PermissionService } from '@auth';

export class UseSurvey {
  private readonly _surveyService = inject(SurveyService);
  private readonly _tagsService = inject(TagsService);
  private readonly _clubService = inject(ClubService);
  private readonly _userPermissions = inject(PermissionService);

  addNewSurvey(formData: FormData): Observable<any> {
    return this._surveyService.create(formData);
  }

  addSurveyVoting(formData: FormData): Observable<any> {
    return this._surveyService.createSurveyVoting(formData);
  }

  updateSurvey(surveyId: number, formData: FormData): Observable<any> {
    return this._surveyService.update(surveyId, formData);
  }

  deleteSurvey(surveyId: number): Observable<any> {
    return this._surveyService.delete(surveyId);
  }

  getSurveyById(surveyId: number): Observable<SurveyEntity> {
    return this._surveyService.get(surveyId).pipe(
      map((response: SurveyEntity) => {
        const transformedResponse = plainToClass(SurveyEntity, response);
        transformedResponse.answers = transformedResponse.surveyAnswer.map((item) => item.surveyAnswer);
        transformedResponse.tags = transformedResponse.surveyTags.map((item) => item.tagName);
        transformedResponse.groupType = transformedResponse.surveyType ? [transformedResponse.surveyType[0].surveyGroup.id] : null;
        if (transformedResponse.image && transformedResponse.id) {
          transformedResponse.imageUrl = GenerateImageUrlWithToken(transformedResponse.id, SectionType.SURVEY);
        }
        return transformedResponse;
      }),
    );
  }

  getSurveyResults(surveyId: number): Observable<SurveyResultDetailsEntity> {
    return this._surveyService.getSurveyResultsById(surveyId).pipe(
      map((res) => {
        const transformedResponse = plainToInstance(SurveyResultDetailsEntity, res);
        const survey = transformedResponse.surveyDetails[0] as SurveyEntity;
        if (survey.image && survey.id) {
          survey.imageUrl = GenerateImageUrlWithToken(survey.id, SectionType.SURVEY);
        }
        survey.option = +survey.option;
        // survey.additionalCastVote = survey.additionalCastVote?.toString() === 'true';

        if (survey.additionalCastVote && survey.additionalCastVote.toString() === 'true') {
          survey.additionalCastVote = false;
        } else if ((survey.additionalCastVote && survey.additionalCastVote.toString() == 'false') || (survey.additionalCastVote == null && survey.surveyResults && survey.surveyResults.length > 0)) {
          survey.additionalCastVote = survey.surveyResults.some((obj: any) => obj.userId === +GetUserId());
        }

        survey.additionalAnonymousVoting = survey.additionalAnonymousVoting?.toString() === 'true';
        transformedResponse.surveyDetails = [survey];
        return transformedResponse;
      }),
    );
  }

  getUserVotedSurveyResults(surveyId: number): Observable<SurveyResultsEntity[]> {
    return this._surveyService.getSurveyResultsByUserId(surveyId, +GetUserId()).pipe(
      map((res) => {
        return res.map((item: SurveyResultsEntity) => plainToInstance(SurveyResultsEntity, item)) as SurveyResultsEntity[];
      }),
    );
  }

  getSurveyTags(): Observable<TagEntity[]> {
    return this._tagsService.find(null, { type: TagType.SURVEY }).pipe(
      map((response) => {
        return response.map((item: TagEntity) => {
          return plainToClass(TagEntity, item);
        }) as TagEntity[];
      }),
    );
  }

  getTeamGroups(): Observable<GroupEntity[]> {
    const teamId = +GetTeamId();
    return this._clubService.groups(teamId).pipe(
      map((response) => {
        return response.map((item: GroupEntity) => plainToClass(GroupEntity, item)) as GroupEntity[];
      }),
    );
  }

  getAllActiveSurveys(pagination: Partial<Pagination>, queryParams?: FilterQuery): Observable<SurveysList> {
    const serviceCall = this._userPermissions.isAdmin()
      ? this._surveyService.getActiveSurveys(pagination, queryParams)
      : this._surveyService.getActivePollSurveyUserById(GetUserId(), pagination, queryParams);

    return serviceCall.pipe(map((response) => this._transformSurveyResponse(response)));
  }

  getAllCompletedSurveys(pagination: Partial<Pagination>, queryParams?: FilterQuery): Observable<SurveysList> {
    const serviceCall = this._userPermissions.isAdmin() ? this._surveyService.getCompletedSurveys(pagination, queryParams) : this._surveyService.getCompletedSurveyByUserId(GetUserId(), pagination);

    return serviceCall.pipe(map((response) => this._transformSurveyResponse(response)));
  }

  getAllMySurveys(pagination: Partial<Pagination>, queryParams?: FilterQuery): Observable<SurveysList> {
    return this._surveyService.getMySurveys(pagination, queryParams).pipe(map((response) => this._transformSurveyResponse(response, true)));
  }

  getVotingResult(surveyId: number, answerId: number): Observable<any> {
    return this._surveyService.getCastedVote(surveyId, answerId).pipe(
      map((response) => {
        return plainToInstance(SurveyAnswerDetailsEntity, response);
      }),
    );
  }

  activeCloseSurvey(surveyId: number): Observable<any> {
    return this._surveyService.activeCloseStatusUpdate(surveyId);
  }

  private _transformSurveyResponse(response: any, isNested = false): SurveysList | null {
    if (response) {
      return {
        pagination: response.pagination,
        survey: response.survey.map((item: any) => {
          const surveyData = isNested ? item.survey : item;
          const transformedEntity = plainToClass(SurveyEntity, surveyData as SurveyEntity);
          if (transformedEntity?.owner?.deleted) {
            transformedEntity.owner.firstName = 'Deleted';
            transformedEntity.owner.lastName = 'User';
            transformedEntity.owner.memberId = '0';
          }
          return transformedEntity;
        }),
      } as SurveysList;
    }
    return null;
  }
}
